// Mercapoli - Página Pública de Menú (Embebible)
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, ShoppingCart, Plus, Minus, MapPin, Clock, Star,
  ChevronRight, X, Truck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const PublicMenu = () => {
  const { merchantId } = useParams();
  const [searchParams] = useSearchParams();
  
  const embed = searchParams.get('embed') === 'true';
  const theme = searchParams.get('theme') || 'light';
  const lang = searchParams.get('lang') || 'es';
  
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const translations = {
    es: {
      addToCart: 'Agregar',
      viewCart: 'Ver carrito',
      checkout: 'Ordenar ahora',
      items: 'artículos',
      subtotal: 'Subtotal',
      delivery: 'Envío',
      total: 'Total',
      emptyCart: 'Tu carrito está vacío',
      orderNow: '¡Ordena ahora!',
      minOrder: 'Pedido mínimo',
      deliveryTime: 'Tiempo de entrega',
      poweredBy: 'Powered by'
    },
    en: {
      addToCart: 'Add',
      viewCart: 'View cart',
      checkout: 'Order now',
      items: 'items',
      subtotal: 'Subtotal',
      delivery: 'Delivery',
      total: 'Total',
      emptyCart: 'Your cart is empty',
      orderNow: 'Order now!',
      minOrder: 'Min. order',
      deliveryTime: 'Delivery time',
      poweredBy: 'Powered by'
    }
  };

  const t = translations[lang] || translations.es;

  useEffect(() => {
    loadMenu();
  }, [merchantId]);

  const loadMenu = async () => {
    try {
      const response = await fetch(`${API_URL}/api/widget/menu/${merchantId}`);
      if (!response.ok) throw new Error('Menu not found');
      const data = await response.json();
      setMenuData(data);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
    setLoading(false);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.producto_id === product.producto_id);
      if (existing) {
        return prev.map(i => 
          i.producto_id === product.producto_id 
            ? { ...i, cantidad: i.cantidad + 1 }
            : i
        );
      }
      return [...prev, { ...product, cantidad: 1 }];
    });
    toast.success(`${product.nombre} agregado`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => {
      const updated = prev.map(item => {
        if (item.producto_id === productId) {
          const newQty = item.cantidad + delta;
          return newQty > 0 ? { ...item, cantidad: newQty } : null;
        }
        return item;
      }).filter(Boolean);
      return updated;
    });
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.cantidad, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    
    setCheckingOut(true);
    try {
      const response = await fetch(`${API_URL}/api/widget/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: merchantId,
          ciudad_id: menuData?.merchant?.ciudad_id,
          items: cart.map(item => ({
            producto_id: item.producto_id,
            nombre: item.nombre,
            precio: item.precio,
            cantidad: item.cantidad,
            notas: ''
          })),
          origin_domain: window.location.hostname
        })
      });
      
      if (!response.ok) throw new Error('Checkout failed');
      const data = await response.json();
      
      // Redirigir al checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error al procesar. Intenta de nuevo.');
    }
    setCheckingOut(false);
  };

  const currency = menuData?.currency || { symbol: 'Q' };
  const merchant = menuData?.merchant || {};

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <p>Menú no encontrado</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header con banner */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={merchant.banner || 'https://via.placeholder.com/800x200/22c55e/ffffff?text=Menu'} 
          alt={merchant.nombre}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-end gap-4">
            <img 
              src={merchant.logo || 'https://via.placeholder.com/80x80/22c55e/ffffff?text=M'}
              alt=""
              className="w-16 h-16 rounded-xl border-4 border-white shadow-lg object-cover"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">{merchant.nombre}</h1>
              <div className="flex items-center gap-3 text-white/90 text-sm mt-1">
                <span className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  {merchant.calificacion || '4.5'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {merchant.tiempo_entrega}
                </span>
                <span className="flex items-center gap-1">
                  <Truck className="h-4 w-4" />
                  {currency.symbol}{merchant.costo_envio?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className={`max-w-2xl mx-auto p-4 pb-32`}>
        {/* Categorías y productos */}
        {menuData.categorias?.map((categoria, catIdx) => (
          <div key={catIdx} className="mb-8">
            <h2 className={`text-lg font-bold mb-4 pb-2 border-b-2 border-green-500 ${theme === 'dark' ? 'text-white' : ''}`}>
              {categoria.nombre}
            </h2>
            <div className="space-y-3">
              {categoria.productos?.map((producto) => {
                const inCart = cart.find(i => i.producto_id === producto.producto_id);
                
                return (
                  <motion.div
                    key={producto.producto_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-800 hover:bg-gray-700' 
                        : 'bg-white hover:shadow-md'
                    }`}
                    onClick={() => !inCart && addToCart(producto)}
                  >
                    <img 
                      src={producto.imagen || 'https://via.placeholder.com/80x80/f3f4f6/9ca3af?text=🍽'}
                      alt={producto.nombre}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold">{producto.nombre}</h3>
                      <p className={`text-sm line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {producto.descripcion}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-green-500 font-bold">
                          {currency.symbol}{producto.precio?.toFixed(2)}
                        </span>
                        {inCart ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={() => updateQuantity(producto.producto_id, -1)}
                              className="w-8 h-8 rounded-full border-2 border-green-500 text-green-500 flex items-center justify-center hover:bg-green-500 hover:text-white transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-bold w-6 text-center">{inCart.cantidad}</span>
                            <button 
                              onClick={() => updateQuantity(producto.producto_id, 1)}
                              className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                            <Plus className="h-4 w-4 mr-1" />
                            {t.addToCart}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Powered by */}
        <div className="text-center text-sm text-gray-400 mt-8">
          {t.poweredBy} <a href="https://mercapoliapp.com" target="_blank" rel="noopener noreferrer" className="text-green-500 font-semibold hover:underline">Mercapoli</a>
        </div>
      </div>

      {/* Carrito flotante */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-black/70 backdrop-blur-lg"
          >
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-3 text-white">
                <span className="text-sm">{getCartCount()} {t.items}</span>
                <span className="font-bold text-lg">{currency.symbol}{getCartTotal().toFixed(2)}</span>
              </div>
              <Button 
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-6 text-lg"
                onClick={handleCheckout}
                disabled={checkingOut}
              >
                {checkingOut ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ShoppingCart className="h-5 w-5 mr-2" />
                )}
                {t.checkout}
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PublicMenu;
